@if (selectedTestObject) {
  <div class="overview-page">
    <section class="overview-header">
      <div class="header-main">
        <wm-testobject-type-icon [origin]="canonicalTestObject?.origin"></wm-testobject-type-icon>
        <div class="header-copy">
          @if (!editMode) {
            <h2>{{ getName() }}</h2>
          } @else {
            <input pInputText class="info-input" placeholder="Name of this Test Object" [(ngModel)]="selectedTestObject.target.name">
          }

          @if (!editMode) {
            @if (getDescription()) {
              <div class="supporting-copy">{{ getDescription() }}</div>
            }
          } @else {
            <textarea pTextarea class="info-input" placeholder="Description of this Test Object" [(ngModel)]="selectedTestObject.target.description"></textarea>
          }
        </div>

        <div class="header-actions">
          @if (getCanonicalTestObjectDetailQueryParams(); as queryParams) {
            <a class="testobject-detail-link"
               [routerLink]="getCanonicalTestObjectDetailLink()"
               [queryParams]="queryParams"
               [pTooltip]="'Open Test Object details'"
               tooltipPosition="top"
               aria-label="Open Test Object details">
              <fa-icon [icon]="faExternalLink" [fixedWidth]="true"></fa-icon>
            </a>
          }

          @if (!readOnly) {
            @if (!editMode) {
              <p-button class="edit"
                        styleClass="p-button-outlined p-button-small"
                        (click)="editTestObject(selectedTestObject.testObjectId)" icon="true">
                <fa-icon icon="pen"></fa-icon>
              </p-button>
              <p-button class="delete"
                        styleClass="p-button-outlined p-button-small p-button-danger"
                        (click)="removeTestObject(selectedTestObject.testObjectId)" icon="true">
                <fa-icon icon="trash"></fa-icon>
              </p-button>
            } @else {
              <p-button class="discard"
                        styleClass="p-button-outlined p-button-small p-button-secondary"
                        (click)="discardChanges()" icon="true">
                <fa-icon icon="times"></fa-icon>
              </p-button>
              <p-button class="save"
                        styleClass="p-button-outlined p-button-small"
                        (click)="saveChanges()" icon="true">
                <fa-icon icon="save"></fa-icon>
              </p-button>
            }
          }

          @if (closable) {
            <button pButton type="button" class="p-button-text p-button-sm close" (click)="onClose.emit()">
              <fa-icon [icon]="faTimes"></fa-icon>
            </button>
          }
        </div>
      </div>

      @if (hasSourceDetails()) {
        <details class="details-disclosure">
          <summary>
            <fa-icon [icon]="faChevronRight" class="details-chevron"></fa-icon>
            <span>Source details</span>
          </summary>
          <div class="details-grid">
            <div class="meta-item">
              <div class="meta-label">Created from</div>
              <div class="meta-value">{{ getOriginLabel() }}</div>
              <div class="meta-support">{{ getOriginDescription() }}</div>
            </div>
            <div class="meta-item">
              <div class="meta-label">Source</div>
              <div class="meta-value">{{ getGenerationLabel() }}</div>
              <div class="meta-support">{{ getGenerationDescription() }}</div>
            </div>
          </div>
        </details>
      }
    </section>

    @if (shouldShowLabelsSection()) {
      <section class="content-section compact-section labels-section">
        <div class="section-header labels-header">
          <div>
            <h3>Labels</h3>
          </div>

          @if (!readOnly) {
            <div class="editable-labels">
              <wm-tags [tags]="tags" (tagsChanged)="setTagsForSelectedTestObject($event)"
                       [onlyLocal]="true"></wm-tags>
            </div>
          }
        </div>

        @if (readOnly) {
          <div class="label-list">
            @for (label of getLabelStrings(); track label) {
              <span class="label-chip">{{ label }}</span>
            }
          </div>
        }
      </section>
    }

    @if (shouldShowLatestStateSection()) {
    <section class="content-section">
      <div class="section-header">
        <div>
          <h3>Latest state</h3>
          <p>Representative preview.</p>
        </div>
      </div>

      @if (selectedObjectState; as state) {
        <div class="state-overview" [ngClass]="{'with-preview': hasStatePreview()}">
          <div class="state-copy">
            <div class="state-kicker">Captured page</div>
            <div class="state-primary">{{ getLatestStatePrimaryLabel() }}</div>

            @if (getLatestStateSecondaryLabel(); as secondaryLabel) {
              <div class="state-secondary">{{ secondaryLabel }}</div>
            }

            <div class="state-meta-grid">
              <div class="meta-item">
                <div class="meta-label">Captured</div>
                <div class="meta-value">{{ state.start | wmDateFormat: 'YYYY-MM-DD HH:mm' }}</div>
              </div>

              @if (getLatestStateStepCount(); as stepCount) {
                <div class="meta-item">
                  <div class="meta-label">Recorded steps</div>
                  <div class="meta-value">{{ stepCount }} {{ stepCount === 1 ? 'step' : 'steps' }}</div>
                </div>
              }
            </div>

            @if (hasStateMediaSummary()) {
              <div class="state-summary-chips">
                @for (mediaItem of getStateMediaSummaryItems(); track mediaItem.label) {
                  <span class="summary-chip" [ngClass]="mediaItem.className">
                    <fa-icon [icon]="mediaItem.icon"></fa-icon>
                    <span>{{ mediaItem.label }}</span>
                  </span>
                }
              </div>
            }

            <div class="state-actions">
              @if (canOpenStateInWorkbench()) {
                <button pButton type="button" class="p-button-outlined p-button-sm"
                        (click)="openInWorkbench()">
                  <fa-icon [icon]="faTv" [fixedWidth]="true"></fa-icon>
                  <span>Open in Workbench</span>
                </button>
              }

              @if (selectedObjectStateArtifacts['DOM.DomLayoutTree']; as artifacts) {
                @for (art of artifacts; track art.id) {
                  <p-button styleClass="p-button-outlined p-button-sm" label="View layout"
                            (click)="viewState({id: state.id, layoutTreeId: art.data['layoutTreeId']})"></p-button>
                }
              }
            </div>
          </div>

          @if (hasStatePreview()) {
            <div class="state-preview">
              <wm-clipping-window>
                <wm-image [allowToOpenScreenshotToolInPopup]="true"
                          [imageUrl]="state.firstScreenshotUrl || undefined"
                          [imageId]="state.firstScreenshotId || undefined"
                ></wm-image>
              </wm-clipping-window>
            </div>
          }
        </div>

        @if (selectedObjectStateArtifacts['Page.A11yReport']; as a11yReport) {
          <details class="details-disclosure artifact-disclosure">
            <summary>
              <fa-icon [icon]="faChevronRight" class="details-chevron"></fa-icon>
              <span>Accessibility report</span>
              <span class="disclosure-summary" [ngClass]="{'is-error': getA11yViolationCount() > 0}">
                {{ getA11ySummaryText() }}
              </span>
            </summary>
            <div class="artifact-content">
              <wm-a11yreport-artifact [artifact]="a11yReport[0]"></wm-a11yreport-artifact>
            </div>
          </details>
        }
      } @else {
        <div class="empty-panel">
          <div class="empty-panel-layout" [ngClass]="{'has-preview': hasReferenceImage()}">
            <div class="empty-panel-copy">
              <div class="empty-title">No captured states yet</div>
              <div class="empty-copy">Generate a state snapshot to show a preview here.</div>

              @if (shouldShowCaptureAction()) {
                <div class="empty-panel-actions">
                  <p-button styleClass="p-button-outlined"
                            [disabled]="isCaptureRunning()"
                            (click)="captureRepresentativeState()">
                    <fa-icon [icon]="faSyncAlt"></fa-icon>
                    <span>Generate state snapshot</span>
                  </p-button>
                </div>
              }

              @if (isCaptureRunning()) {
                <div class="capture-status running">
                  <div class="capture-status-title">
                    <p-progressSpinner strokeWidth="4"></p-progressSpinner>
                    <span>Generating state snapshot</span>
                  </div>
                  <div class="capture-status-message">{{ getCaptureProgressMessage() }}</div>
                  <p-progressBar [value]="getCaptureProgressPercent()" [showValue]="true"></p-progressBar>
                </div>
              }

              @if (isCaptureFailed()) {
                <div class="capture-status failed">
                  <div class="capture-status-title">State snapshot generation failed</div>
                  <div class="capture-status-message">{{ getCaptureProgressMessage() }}</div>
                </div>
              }

              @if (shouldShowUnavailableCaptureHint()) {
                <div class="empty-state-note">{{ stateCaptureUnavailableReason }}</div>
              }
            </div>

            @if (hasReferenceImage()) {
              <div class="empty-panel-preview">
                <div class="preview-label">Reference image</div>
                <wm-clipping-window>
                  <wm-image [editable]="true" [scale]="true" [imageUrl]="imageUrl"></wm-image>
                </wm-clipping-window>
              </div>
            }
          </div>
        </div>
      }
    </section>
    }
  </div>
}
